import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeBreakpointIndexEnum } from '@inter-ikea-kompis/enums';
import { ThemeBreakpointObserver } from '@inter-ikea-kompis/themes';
import type {
	ILocalizedInformation,
	IShoppingProduct,
	ITheme,
	ITotalPriceSummary
} from '@inter-ikea-kompis/types';
import {
	DexfSettingsUtility,
	PriceUtility,
	TotalPriceCalculator
} from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { PriceFormatter } from '@inter-ikea-kompis/component-price-formatter';
import { PriceModuleLayoutTypeEnum } from '@inter-ikea-kompis/component-price-module';
import { PriceModule } from '@inter-ikea-kompis/component-price-module';
import { ProductRowLayoutEnum } from '@inter-ikea-kompis/component-product-row';

import ProductListModalEnum from '../../enums/ProductListModalEnum.js';
import type IProductListDexfSettings from '../../types/IProductListDexfSettings.js';
import type IProductListTranslations from '../../types/IProductListTranslations.js';
import ProductListTotalPriceStyle from './ProductListTotalPriceStyle.js';

/**
 *
 *
 * @event modalClose Triggered when the modal has been closed
 * @event modalOpen Triggered when a modal has been opened. event.detail.modalType tells you the type
 */

export default class ProductListTotalPrice extends KompisElement {
	// Theme model
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IProductListTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IProductListDexfSettings | null = null;

	// A list of shopping products.
	@property({ type: Object, required: true })
	public shoppingProducts!: IShoppingProduct[];

	// Whether to show the collect checkbox or not.
	@property({ type: Boolean })
	public collectable = false;

	// Show a modal.
	@property({ type: String })
	public visibleModal: ProductListModalEnum | null = null;

	// Disables links. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	// Layout for product row. NOTE: ProductRowLayoutEnum.compactPrint should only be used during print.
	@property({ type: String })
	public layout: ProductRowLayoutEnum | null = null;

	private breakpointObserver = new ThemeBreakpointObserver(() => this.requestUpdate(), this);

	private dexfSettings: IProductListDexfSettings | null = null;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		// We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslationsAndSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html`${this.getLayout()}`;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-price-module', PriceModule);
		this.customElements.define('kompis-price-formatter', PriceFormatter);

		super.connectedCallback();
	}

	public override disconnectedCallback(): void {
		super.disconnectedCallback();
		this.breakpointObserver.disconnect();
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (changedProperties.has('theme')) {
			this.breakpointObserver.observe(this.theme);
		}
		if (
			changedProperties.has('localizedInformation') &&
			this.localizedInformation &&
			!this.settings
		) {
			this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
		}
		if (changedProperties.has('settings') && this.settings) {
			this.dexfSettings = this.settings;
		}
		return true;
	}

	private getLayout(): RenderResult {
		if (this.layout === ProductRowLayoutEnum.compactPrint) {
			return this.getCompactPrintLayout();
		} else {
			return this.getDefaultLayout();
		}
	}

	private getDefaultLayout(): RenderResult {
		const validShoppingProducts = this.shoppingProducts.filter(
			(shoppingProduct) => shoppingProduct.product.valid
		);
		const totalPrice = TotalPriceCalculator.calculate(
			validShoppingProducts,
			this.getSettings().localisation.dateFormat
		);
		const breakpointIndex =
			this.breakpointObserver.getBreakpointIndex() ?? ThemeBreakpointIndexEnum.small;
		return html`
			<style>
				${ProductListTotalPriceStyle.getStyle({
					theme: this.theme,
					isTroOrFamilyPrice: totalPrice.temporary || totalPrice.family,
					collectable: this.collectable,
					isMobile: breakpointIndex <= ThemeBreakpointIndexEnum.small,
					isTablet:
						breakpointIndex <= ThemeBreakpointIndexEnum.xlarge &&
						breakpointIndex >= ThemeBreakpointIndexEnum.small
				})}
			</style>
			<div class="container">
				<div class="spacer"></div>
				<div class="spacerDesktop"></div>
				<div class="total">
					<p class="text--body-m bold">${this.getTranslations().productListTotalPrice}</p>
					${this.getNumberOfItems(validShoppingProducts)}
				</div>
				${this.getNumberOfItems(validShoppingProducts)}
				<div class="priceDisplay">
					<kompis-price-module
						.theme="${this.theme}"
						.localizedInformation="${this.localizedInformation}"
						.translations="${this.translations}"
						.settings="${this.settings}"
						.shoppingProducts="${validShoppingProducts}"
						.showPriceExclTax="${true}"
						.showRegularPriceExclTax="${true}"
						.layout="${PriceModuleLayoutTypeEnum.listPrice}"
						.showEcoFee="${true}"
						.disableLinks="${this.disableLinks}"
						.visibleModal="${this.visibleModal ? this.visibleModal : null}"
						@modalClose="${() =>
							this.dispatchEvent(
								new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } })
							)}"
						@modalOpen="${() =>
							this.dispatchEvent(
								new CustomEvent('modalOpen', {
									bubbles: true,
									detail: {
										visibleModal: ProductListModalEnum.ecoFeeCard
									}
								})
							)}"
					></kompis-price-module>
				</div>
			</div>
		`;
	}

	private getCompactPrintLayout(): RenderResult {
		const totalPrice = TotalPriceCalculator.calculate(
			this.shoppingProducts,
			this.getSettings().localisation.dateFormat
		);

		const listTotalPrice = PriceUtility.getPrice(totalPrice);

		return html`<style>
				${ProductListTotalPriceStyle.getCompactLayoutStyle({ theme: this.theme })}
			</style>
			${this.getCompactPriceWrapper({ totalPrice, listTotalPrice })}`;
	}

	private getCompactPriceWrapper(options: {
		totalPrice: ITotalPriceSummary;
		listTotalPrice: number;
	}): RenderResult {
		const { priceInfoIkeaFamilyPrice, priceInfoRegularPrice, productListTotalPriceSummary } =
			this.getTranslations();
		const { totalPrice, listTotalPrice } = options;
		return totalPrice.family
			? html`
					<div class="compactPriceWrapper">
						<p class="compactCommercialMessage bold text--body-m">${priceInfoIkeaFamilyPrice}</p>
						<p class="compactPrice bold text--body-m">
							<kompis-price-formatter
								.settings="${this.settings}"
								.localizedInformation="${this.localizedInformation}"
								.amount="${listTotalPrice}"
							></kompis-price-formatter>
						</p>
						<p class="compactCommercialMessage bold text--body-s">${priceInfoRegularPrice}</p>
						<p class="compactPrice bold text--body-s">
							<kompis-price-formatter
								.settings="${this.settings}"
								.localizedInformation="${this.localizedInformation}"
								.amount="${totalPrice.inclTax}"
							></kompis-price-formatter>
						</p>
					</div>
				`
			: html`
					<div class="compactPriceWrapper">
						<p class="compactCommercialMessage bold text--body-m">
							${productListTotalPriceSummary}
						</p>
						<p class="compactPrice bold text--body-m">
							<kompis-price-formatter
								.settings="${this.settings}"
								.localizedInformation="${this.localizedInformation}"
								.amount="${listTotalPrice}"
							></kompis-price-formatter>
						</p>
					</div>
				`;
	}

	private getSettings(): IProductListDexfSettings {
		return <IProductListDexfSettings>this.dexfSettings;
	}

	private hasTranslationsAndSettings(): boolean {
		return !!this.localizedInformation || !!(this.translations && this.settings);
	}

	private getTranslations(): IProductListTranslations {
		const translations = this.translations
			? this.translations
			: this.localizedInformation?.translations;

		return <IProductListTranslations>translations;
	}

	/**
	 * Get number of items.
	 *
	 * @param validShoppingProducts Valid shopping Products.
	 * @returns Template.
	 */
	private getNumberOfItems(validShoppingProducts: IShoppingProduct[]): RenderResult {
		return html`
			<div class="numberOfItems">
				<p class="bold text--body-m">
					${validShoppingProducts.reduce((sum, product) => sum + product.quantity, 0)}
				</p>
				<div class="divider"></div>
				<p class="text--body-m">${this.getTranslations().productListItems}</p>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-product-list-total-price': ProductListTotalPrice;
	}
}
